import Grid from '@mui/material/Grid';
import React, { useContext } from 'react';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import { IUserSchema } from '../../../graphql/type/user';
import Moment from '../../components/Moment';
import SchemaUserAvatar from '../../components/_schema/SchemaUserAvatar';
import Context from '../../context';
import SchemaUserName from '../../components/_schema/SchemaUserName';

interface IProps {
  message: {
    _id: string;
    room_id: string;
    text: string;
    tags: string[];
    created?: {
      time?: Date;
      user?: string;
      userData?: IUserSchema;
    };
  };
}

const avatarWidth = 48;

const useStyles = makeStyles<Theme, { isSender: boolean }>((theme: Theme) => ({
  messageContainer: {
    backgroundColor: ({ isSender }) =>
      isSender ? theme.palette.primary.main : '#fff',
    borderRadius: '25px',
    display: 'inline-block',
    padding: '4px',
    overflowWrap: 'break-word',
    maxWidth: '100%',
    whiteSpace: 'pre-wrap',
  },
  tagContainer: {
    margin: '4px 12px',
  },
  messageTypography: {
    margin: '4px 12px',
    color: ({ isSender }) => (isSender ? '#fff' : 'initial'),
  },
  avatarContainer: {
    width: `${avatarWidth}px`,
  },
  messageGrid: {
    width: `calc(100% - ${avatarWidth}px)`,
  },
  senderTag: {
    backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#000',
    color: theme.palette.primary.main,
  },
  messageInfoContainer: {
    // marginBottom: '2px',
  },
}));

export default function Message(props: IProps) {
  const { t } = useTranslation();
  const { message } = props;
  const { user: contextUser } = useContext(Context);

  const user =
    message.created && message.created.userData
      ? message.created.userData
      : undefined;

  const isSender = contextUser && user ? contextUser._id === user._id : false;
  const classes = useStyles({ isSender });
  const avatarComponent = (
    <Grid item className={classes.avatarContainer}>
      <SchemaUserAvatar user={user} useIndexLink />
    </Grid>
  );

  const messageComponent = (
    <Grid
      item
      container
      className={classes.messageGrid}
      justifyContent={isSender ? 'flex-end' : 'flex-start'}
    >
      {(user || (message.created && message.created.time)) && (
        <Grid
          justifyContent={isSender ? 'flex-end' : 'flex-start'}
          className={classes.messageInfoContainer}
          container
          alignItems="center"
        >
          {user && !isSender && (
            <Typography variant="caption">
              &nbsp;&nbsp;
              <SchemaUserName user={user} />
              {message.created && message.created.time ? (
                <>,&nbsp;</>
              ) : (
                <>&nbsp;&nbsp;</>
              )}
            </Typography>
          )}
          {message.created && message.created.time ? (
            <Typography variant="caption">
              <Moment
                time={message.created.time}
                format="DD MMM YYYY, h:mm a"
                fromNow
              />
            </Typography>
          ) : (
            ''
          )}
        </Grid>
      )}
      <Grid justifyContent={isSender ? 'flex-end' : 'flex-start'} container>
        <Tooltip
          enterDelay={500}
          enterNextDelay={500}
          arrow
          title={
            message.created && message.created.time ? (
              <Moment time={message.created.time} />
            ) : (
              ''
            )
          }
        >
          <span className={classes.messageContainer}>
            {message.tags && message.tags.length > 0 && (
              <span className={classes.tagContainer}>
                {message.tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={`#${tag}`}
                    size="small"
                    className={isSender ? classes.senderTag : undefined}
                    color="primary"
                    // style={{ height: '16px' }}
                  />
                ))}
              </span>
            )}
            <Typography variant="body2" className={classes.messageTypography}>
              {message.text}
            </Typography>
          </span>
        </Tooltip>
      </Grid>
    </Grid>
  );

  return (
    <Grid
      container
      item
      spacing={1}
      alignItems="center"
      justifyContent={isSender ? 'flex-end' : 'flex-start'}
    >
      {isSender ? (
        <>
          {messageComponent}
          {avatarComponent}
        </>
      ) : (
        <>
          {avatarComponent}
          {messageComponent}
        </>
      )}
    </Grid>
  );
}
